import React, { useState, useMemo, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { Logo } from '@/components/ui/Logo';
import { Input } from '@/components/ui/Input';
import {
  LayoutDashboard,
  FileText,
  ClipboardList,
  ChevronRight,
  ChevronDown,
  X,
  Brain,
  Briefcase,
  BookOpen,
  AlertCircle,
  GraduationCap,
  Target,
  Lightbulb,
  Sparkles,
  Zap,
  BookMarked,
  Trophy,
  Mail,
  BarChart3,
  CreditCard,
  Users,
  Upload,
  Shield,
  UserCog,
  Search,
  ChevronLeft,
  MessageSquare,
  Key,
  TrendingUp,
  DollarSign,
  Activity,
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { useAdminAuth } from '@/contexts/AdminAuthContext';

interface MenuItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  path?: string;
  children?: MenuItem[];
  badge?: string;
}

const baseMenuItems: MenuItem[] = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    icon: <LayoutDashboard className="h-5 w-5" />,
    path: '/admin/dashboard',
  },
  {
    id: 'content-management',
    label: 'Content Management',
    icon: <FileText className="h-5 w-5" />,
    children: [
      {
        id: 'contents',
        label: 'Contents',
        icon: <FileText className="h-4 w-4" />,
        path: '/admin/contents',
      },
    ],
  },
  {
    id: 'prompt-library',
    label: 'Prompt Library',
    icon: <MessageSquare className="h-5 w-5" />,
    children: [
      {
        id: 'prompts-list',
        label: 'All prompts',
        icon: <MessageSquare className="h-4 w-4" />,
        path: '/admin/prompts',
      },
      {
        id: 'prompts-write-test',
        label: 'Write & test',
        icon: <FileText className="h-4 w-4" />,
        path: '/admin/prompts/write-test',
      },
    ],
  },
  {
    id: 'survey-management',
    label: 'Survey Management',
    icon: <ClipboardList className="h-5 w-5" />,
    children: [
      {
        id: 'surveys',
        label: 'Surveys',
        icon: <ClipboardList className="h-4 w-4" />,
        path: '/admin/surveys',
      },
    ],
  },
  {
    id: 'skill-knowledge-management',
    label: 'Skill & Knowledge',
    icon: <Brain className="h-5 w-5" />,
    children: [
      {
        id: 'skills',
        label: 'Skills',
        icon: <Brain className="h-4 w-4" />,
        path: '/admin/skills',
      },
      {
        id: 'occupations',
        label: 'Occupations',
        icon: <Briefcase className="h-4 w-4" />,
        path: '/admin/occupations',
      },
      {
        id: 'wins',
        label: 'Wins',
        icon: <Trophy className="h-4 w-4" />,
        path: '/admin/wins',
      },
      {
        id: 'pain-points',
        label: 'Pain Points',
        icon: <AlertCircle className="h-4 w-4" />,
        path: '/admin/pain-points',
      },
    ],
  },
  {
    id: 'learning-management',
    label: 'Learning & Goals',
    icon: <GraduationCap className="h-5 w-5" />,
    children: [
      {
        id: 'learning-plans',
        label: 'Learning Plans',
        icon: <BookMarked className="h-4 w-4" />,
        path: '/admin/learning-plans',
      },
      {
        id: 'nudges',
        label: 'Nudges',
        icon: <Target className="h-4 w-4" />,
        path: '/admin/nudges',
      },
      {
        id: 'tips',
        label: 'Tips',
        icon: <Lightbulb className="h-4 w-4" />,
        path: '/admin/tips',
      },
      {
        id: 'tidbits',
        label: 'Tidbits',
        icon: <Sparkles className="h-4 w-4" />,
        path: '/admin/tidbits',
      },
      {
        id: 'hacks',
        label: 'Hacks',
        icon: <Zap className="h-4 w-4" />,
        path: '/admin/hacks',
      },
      {
        id: 'lessons',
        label: 'Lessons',
        icon: <BookOpen className="h-4 w-4" />,
        path: '/admin/lessons',
      },
      {
        id: 'nanos',
        label: 'Nanos',
        icon: <Sparkles className="h-4 w-4" />,
        path: '/admin/nanos',
      },
      {
        id: 'assessments',
        label: 'Assessments',
        icon: <ClipboardList className="h-4 w-4" />,
        path: '/admin/assessments',
      },
      {
        id: 'reflections',
        label: 'Reflections',
        icon: <Brain className="h-4 w-4" />,
        path: '/admin/reflections',
      },
    ],
  },
  {
    id: 'notification-management',
    label: 'Notifications',
    icon: <Mail className="h-5 w-5" />,
    children: [
      {
        id: 'notification-templates',
        label: 'Templates',
        icon: <Mail className="h-4 w-4" />,
        path: '/admin/notifications/templates',
      },
      {
        id: 'notification-stats',
        label: 'Statistics',
        icon: <BarChart3 className="h-4 w-4" />,
        path: '/admin/notifications/templates/stats',
      },
    ],
  },
  {
    id: 'subscription-management',
    label: 'Subscriptions',
    icon: <CreditCard className="h-5 w-5" />,
    children: [
      {
        id: 'subscription-plans',
        label: 'Plans',
        icon: <CreditCard className="h-4 w-4" />,
        path: '/admin/subscription-plans',
      },
      {
        id: 'subscription-plans-import',
        label: 'Import Plans',
        icon: <Upload className="h-4 w-4" />,
        path: '/admin/subscription-plans/import',
      },
      {
        id: 'subscription-plans-stats',
        label: 'Plan Statistics',
        icon: <BarChart3 className="h-4 w-4" />,
        path: '/admin/subscription-plans/stats',
      },
      {
        id: 'affiliators',
        label: 'Affiliators',
        icon: <Users className="h-4 w-4" />,
        path: '/admin/affiliators',
      },
      {
        id: 'affiliators-stats',
        label: 'Affiliator Stats',
        icon: <BarChart3 className="h-4 w-4" />,
        path: '/admin/affiliators/stats',
      },
      {
        id: 'payment-gateways',
        label: 'Payment Gateways',
        icon: <CreditCard className="h-4 w-4" />,
        path: '/admin/payment-gateways',
      },
    ],
  },
  {
    id: 'permissions-roles',
    label: 'Permissions & Roles',
    icon: <Shield className="h-5 w-5" />,
    children: [
      {
        id: 'permissions',
        label: 'Permissions',
        icon: <Shield className="h-4 w-4" />,
        path: '/admin/permissions',
      },
      {
        id: 'permissions-stats',
        label: 'Permission Stats',
        icon: <BarChart3 className="h-4 w-4" />,
        path: '/admin/permissions/statistics',
      },
      {
        id: 'roles',
        label: 'Roles',
        icon: <UserCog className="h-4 w-4" />,
        path: '/admin/roles',
      },
      {
        id: 'roles-stats',
        label: 'Role Statistics',
        icon: <BarChart3 className="h-4 w-4" />,
        path: '/admin/roles/statistics',
      },
    ],
  },
  {
    id: 'user-profiles',
    label: 'User Profiles',
    icon: <Users className="h-5 w-5" />,
    children: [
      {
        id: 'profiles',
        label: 'User Profiles',
        icon: <Users className="h-4 w-4" />,
        path: '/admin/profiles',
      },
    ],
  },
  {
    id: 'analytics',
    label: 'Analytics',
    icon: <TrendingUp className="h-5 w-5" />,
    children: [
      {
        id: 'analytics-revenue',
        label: 'Revenue Dashboard',
        icon: <DollarSign className="h-4 w-4" />,
        path: '/admin/analytics/revenue',
      },
      {
        id: 'analytics-subscriptions',
        label: 'Subscriptions',
        icon: <CreditCard className="h-4 w-4" />,
        path: '/admin/analytics/subscriptions',
      },
      {
        id: 'analytics-users',
        label: 'User Report',
        icon: <Activity className="h-4 w-4" />,
        path: '/admin/analytics/users',
      },
      {
        id: 'analytics-llm-costs',
        label: 'LLM Costs',
        icon: <Brain className="h-4 w-4" />,
        path: '/admin/analytics/llm-costs',
      },
    ],
  },
];

/** API Keys menu item — only shown to super admins. */
const apiKeysMenuItem: MenuItem = {
  id: 'api-keys',
  label: 'API Keys',
  icon: <Key className="h-5 w-5" />,
  path: '/admin/api-keys',
};

interface AdminSidebarProps {
  isOpen: boolean;
  onToggle: () => void;
  onCollapseChange?: (collapsed: boolean) => void;
}

export const AdminSidebar: React.FC<AdminSidebarProps> = ({ isOpen, onToggle, onCollapseChange }) => {
  const location = useLocation();
  const { isSuperAdmin } = useAdminAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [expandedItems, setExpandedItems] = useState<string[]>([]);

  /** Full menu: base items + API Keys only for super admin. */
  const menuItems = useMemo(
    () => (isSuperAdmin ? [...baseMenuItems, apiKeysMenuItem] : baseMenuItems),
    [isSuperAdmin]
  );

  // Find active menu item and its parent (defined early so useEffect can use it)
  const findActiveMenuItem = useCallback(
    (items: MenuItem[], pathname: string, parentId?: string): { item: MenuItem; parentId?: string } | null => {
      for (const item of items) {
        if (item.path && (pathname === item.path || pathname.startsWith(item.path + '/'))) {
          return { item, parentId };
        }
        if (item.children) {
          const found = findActiveMenuItem(item.children, pathname, item.id);
          if (found) return found;
        }
      }
      return null;
    },
    []
  );

  // Notify parent of collapse state change
  React.useEffect(() => {
    onCollapseChange?.(isCollapsed);
  }, [isCollapsed, onCollapseChange]);

  // Auto-expand section containing active route
  React.useEffect(() => {
    const activeItem = findActiveMenuItem(menuItems, location.pathname);
    if (activeItem?.parentId) {
      setExpandedItems((prev) =>
        prev.includes(activeItem.parentId!) ? prev : [...prev, activeItem.parentId!]
      );
    }
  }, [location.pathname, findActiveMenuItem, menuItems]);

  const toggleExpand = (itemId: string) => {
    setExpandedItems((prev) =>
      prev.includes(itemId) ? prev.filter((id) => id !== itemId) : [...prev, itemId]
    );
  };

  const isActive = (path?: string) => {
    if (!path) return false;
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  // Filter menu items based on search
  const filteredMenuItems = useMemo(() => {
    if (!searchQuery.trim()) return menuItems;
    
    const query = searchQuery.toLowerCase();
    return menuItems.filter((item) => {
      const matchesLabel = item.label.toLowerCase().includes(query);
      const matchesChildren = item.children?.some(
        (child) => child.label.toLowerCase().includes(query)
      );
      return matchesLabel || matchesChildren;
    }).map((item) => {
      if (item.children) {
        return {
          ...item,
          children: item.children.filter((child) =>
            child.label.toLowerCase().includes(query)
          ),
        };
      }
      return item;
    });
  }, [searchQuery, menuItems]);

  const renderMenuItem = (item: MenuItem, level = 0) => {
    const hasChildren = item.children && item.children.length > 0;
    const isExpanded = expandedItems.includes(item.id);
    const active = isActive(item.path);

    if (hasChildren) {
      return (
        <div key={item.id} className="space-y-0.5">
          <button
            onClick={() => toggleExpand(item.id)}
            aria-expanded={isExpanded}
            aria-controls={`submenu-${item.id}`}
            aria-label={`${item.label} menu, ${isExpanded ? 'expanded' : 'collapsed'}`}
            className={cn(
              'w-full flex items-center justify-between px-3 py-2 rounded-md text-sm font-medium transition-all duration-200',
              'hover:bg-accent hover:text-accent-foreground',
              level > 0 && 'pl-6',
              active && 'bg-accent text-accent-foreground',
              isCollapsed && 'justify-center px-2'
            )}
            title={isCollapsed ? item.label : undefined}
          >
            <div className={cn('flex items-center gap-2.5', isCollapsed && 'justify-center')}>
              <span className={cn('flex-shrink-0', isCollapsed && 'mx-auto')}>{item.icon}</span>
              {!isCollapsed && <span className="truncate">{item.label}</span>}
            </div>
            {!isCollapsed && (
              <span className="flex-shrink-0">
                {isExpanded ? (
                  <ChevronDown className="h-4 w-4" />
                ) : (
                  <ChevronRight className="h-4 w-4" />
                )}
              </span>
            )}
          </button>
          {!isCollapsed && isExpanded && (
            <div 
              id={`submenu-${item.id}`} 
              role="menu" 
              className="space-y-0.5 pl-2 animate-in slide-in-from-top-2 duration-200"
            >
              {item.children?.map((child) => renderMenuItem(child, level + 1))}
            </div>
          )}
        </div>
      );
    }

    return (
      <Link
        key={item.id}
        to={item.path || '#'}
        aria-label={item.label}
        aria-current={active ? 'page' : undefined}
        className={cn(
          'flex items-center gap-2.5 px-3 py-2 rounded-md text-sm font-medium transition-all duration-200',
          'hover:bg-accent hover:text-accent-foreground',
          level > 0 && 'pl-6',
          active && 'bg-accent text-accent-foreground font-semibold',
          isCollapsed && 'justify-center px-2'
        )}
        title={isCollapsed ? item.label : undefined}
        onClick={() => {
          if (window.innerWidth < 1024) {
            onToggle();
          }
        }}
      >
        <span className={cn('flex-shrink-0', isCollapsed && 'mx-auto')}>{item.icon}</span>
        {!isCollapsed && <span className="truncate">{item.label}</span>}
      </Link>
    );
  };

  const sidebarWidth = isCollapsed ? 'w-16' : 'w-64';

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={onToggle}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          'fixed top-16 left-0 h-[calc(100vh-4rem)] bg-card border-r border-border z-40 transition-all duration-300',
          'flex flex-col shadow-lg',
          isOpen ? 'translate-x-0' : '-translate-x-full',
          sidebarWidth
        )}
      >
        {/* Sidebar Header */}
        <div className="flex items-center justify-between p-3 border-b border-border min-h-[64px]">
          {!isCollapsed && (
            <Logo variant="icon" className="h-8 w-8" />
          )}
          <div className="flex items-center gap-2">
            {!isCollapsed && (
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={() => setIsCollapsed(true)}
                className="hidden lg:flex h-8 w-8"
                title="Collapse sidebar"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
            )}
            {isCollapsed && (
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={() => setIsCollapsed(false)}
                className="hidden lg:flex h-8 w-8"
                title="Expand sidebar"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            )}
            <Button variant="ghost" size="icon" onClick={onToggle} className="lg:hidden h-8 w-8">
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Search (only when expanded) */}
        {!isCollapsed && (
          <div className="p-3 border-b border-border">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search menu..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-8 h-9 text-sm"
              />
            </div>
          </div>
        )}

        {/* Navigation */}
        <nav 
          className="flex-1 overflow-y-auto overflow-x-hidden p-2 space-y-1" 
          role="navigation" 
          aria-label="Admin navigation"
        >
          {filteredMenuItems.length > 0 ? (
            filteredMenuItems.map((item) => renderMenuItem(item))
          ) : (
            <div className="px-3 py-8 text-center text-sm text-muted-foreground text-sm">
              No results found
            </div>
          )}
        </nav>

        {/* Footer */}
        {!isCollapsed && (
          <div className="p-3 border-t border-border text-xs text-muted-foreground">
            <p className="truncate">Five AI Admin</p>
            <p className="mt-0.5">v1.0.0</p>
          </div>
        )}
        {isCollapsed && (
          <div className="p-2 border-t border-border">
            <div className="text-[10px] text-muted-foreground text-center">v1.0.0</div>
          </div>
        )}
      </aside>
    </>
  );
};
